// Copyright (c) Ping. All rights reserved.

import { useCallback, useMemo } from 'react';

import {
  type ActivityCta,
  ActivityFeedScreen,
  type ActivityItemRecord,
  useSession,
} from '@ping/ui';

import {
  webReadPointersPersister,
  webRemindersPersister,
} from '@core/persistence/db';
import { requestChatJump } from '@features/chat/chatJumpTarget';
import { sendReadPointer } from '@features/readState/deviceGroupReadSync';
import { sendReminderSet } from '@features/reminders/deviceGroupReminderSync';
import { useReminderScheduler } from '@features/reminders/useReminderScheduler';
import { pushChatRouteId } from '@shell/hosts/chatRouteUrl';
import { useRouter } from '@shell/RouterContext';

import {
  clearForcedUnread,
  dismissActivity,
  forceActivityUnread,
  loadActivityFeed,
  subscribeActivityFeed,
} from './activityFeed';

export interface ActivityPanelProps {
  /** When provided, the screen shows a close button (after Sort) that calls
   *  this — used when the feed is an in-panel TabView scene (the chat-header
   *  bell swaps the conversation list for the feed). Omitted for the standalone
   *  `/activity` route. */
  readonly onClose?: () => void;
}

/**
 * Activity feed wired with the client-derived loader/subscriber from
 * `./activityFeed`. The feed is projected locally from the message / reaction /
 * poll / call state in Dexie and re-renders live via `liveQuery`. Read state +
 * reminders ARE synced across the user's devices over the DeviceGroup (§2.10).
 *
 * Rendered by both the `/activity` route (`ActivityRouteHost`) and the in-panel
 * TabView swap in `ChatRouteHost` (via the header bell).
 *
 * BE seam: `registerActivityPushSource` (same module) lets a future server push
 * override the derived feed without touching this panel or the screen.
 */
export function ActivityPanel({ onClose }: ActivityPanelProps = {}): JSX.Element {
  const router = useRouter();
  const { client } = useSession();

  const openConversation = useCallback(
    (conversationId: string | null, messageId?: string): void => {
      if (conversationId === null) return;
      // Record the jump BEFORE navigating so the freshly-selected WebChatRoom
      // reveals the exact message once its messages load — scrolling the
      // timeline to it, or opening the thread panel when it's a thread reply.
      requestChatJump({ conversationId, ...(messageId !== undefined ? { messageId } : {}) });
      router.push('/chat');
      pushChatRouteId(conversationId);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
      onClose?.();
    },
    [router, onClose],
  );

  const convOf = (item: ActivityItemRecord): string | null => {
    try {
      const p = JSON.parse(item.routePayload) as { conversationId?: string };
      return typeof p.conversationId === 'string' ? p.conversationId : item.conversationId;
    } catch {
      return item.conversationId;
    }
  };
  const messageOf = (item: ActivityItemRecord): string | undefined => {
    try {
      const p = JSON.parse(item.routePayload) as { messageId?: string };
      return typeof p.messageId === 'string' ? p.messageId : undefined;
    } catch {
      return undefined;
    }
  };

  // Mark one item read = advance its conversation's read pointer to the item's
  // timestamp (monotonic) and sync it across devices; also clear any per-item
  // forced-unread override.
  const markReadAt = useCallback(
    (conversationId: string, lastReadAt: number, lastReadMessageId?: string): void => {
      const record = {
        conversationId,
        lastReadAt,
        updatedAt: Date.now(),
        ...(lastReadMessageId !== undefined ? { lastReadMessageId } : {}),
      };
      void webReadPointersPersister.put(record);
      void sendReadPointer(client, record);
    },
    [client],
  );

  const onMarkRead = useCallback(
    (item: ActivityItemRecord): void => {
      const conv = convOf(item);
      if (conv !== null) markReadAt(conv, item.timestamp, messageOf(item));
      void clearForcedUnread(item.id);
    },
    [markReadAt],
  );

  const onMarkUnread = useCallback((item: ActivityItemRecord): void => {
    void forceActivityUnread(item.id);
  }, []);

  const onMarkAllRead = useCallback((): void => {
    void loadActivityFeed('all', 'mostRecent').then((items) => {
      const newestByConv = new Map<string, number>();
      for (const it of items) {
        const conv = convOf(it);
        if (conv === null) continue;
        const prev = newestByConv.get(conv);
        if (prev === undefined || it.timestamp > prev) newestByConv.set(conv, it.timestamp);
        void clearForcedUnread(it.id);
      }
      for (const [conv, ts] of newestByConv) markReadAt(conv, ts);
    });
  }, [markReadAt]);

  const onRemind = useCallback(
    (item: ActivityItemRecord, fireAt: number): void => {
      const conv = convOf(item);
      if (conv === null) return;
      const messageId = messageOf(item);
      const record = {
        id: cryptoRandomHex(),
        conversationId: conv,
        fireAt,
        createdAt: Date.now(),
        ...(messageId !== undefined ? { messageId } : {}),
      };
      void webRemindersPersister.put(record);
      void sendReminderSet(client, record);
    },
    [client],
  );

  const onErase = useCallback((item: ActivityItemRecord): void => {
    void dismissActivity(item.id);
  }, []);

  const onGoTo = useCallback(
    (item: ActivityItemRecord): void => openConversation(convOf(item), messageOf(item)),
    [openConversation],
  );
  const onCtaPress = useCallback(
    (item: ActivityItemRecord, _cta: ActivityCta): void =>
      openConversation(convOf(item), messageOf(item)),
    [openConversation],
  );

  // Fire due reminders locally; on fire, surface the conversation.
  const onReminderFire = useCallback(
    (r: { conversationId: string }) => openConversation(r.conversationId),
    [openConversation],
  );
  useReminderScheduler(onReminderFire);

  const actions = useMemo(
    () => ({
      onItemPress: onGoTo,
      onGoToMessage: onGoTo,
      onMarkRead,
      onMarkUnread,
      onMarkAllRead,
      onErase,
      onRemind,
      onCtaPress,
    }),
    [onGoTo, onMarkRead, onMarkUnread, onMarkAllRead, onErase, onRemind, onCtaPress],
  );

  return (
    <ActivityFeedScreen
      load={loadActivityFeed}
      subscribe={subscribeActivityFeed}
      {...(onClose !== undefined ? { onClose } : {})}
      {...actions}
    />
  );
}

/** 16-byte random hex (reminder ids). Falls back to a time+random string when
 *  `crypto` is unavailable. */
function cryptoRandomHex(): string {
  try {
    const b = new Uint8Array(16);
    crypto.getRandomValues(b);
    return [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  } catch {
    return `r${Date.now().toString(16)}${Math.floor(Math.random() * 1e9).toString(16)}`;
  }
}
